import HttpFactory from '../factory'
import type { EpisodeOfCareMetric, Observation } from '../types/observation'
import { useObservationsStore } from '@/stores/observations'

type Tracker = {
  paused: boolean
  code: string[]
  from: Date
  to: Date
  hasReachedTwoWeeks: boolean
  progressCallBack: ((items: any[], code: string) => void) | null
}

class ObservationsModule extends HttpFactory<any[]> {
  private PAGE_SIZE = '1000'

  private PATH_METRICS = '/observations/metrics/patients'
  private PATH_OBSERVATIONS = '/v2/observations'
  private PATH_OBSERVATIONS_COUNT = '/v2/observations/counts'
  private PATH_OBSERVATIONS_CODES = '/v2/observations/codes'
  private PATH_OBSERVATIONS_CARDIOLINE = '/v2/observations/cardioline/patients'
  private PATH_OBSERVATIONS_STETHOME = '/observations/stethome'
  private PATH_STETHOME = 'https://prod.media-api.stethome.com/v2'

  private getByPageController: AbortController = new AbortController()

  private abortControllers: Record<string, AbortController> = {}
  private pausedTracker: Record<string, Record<string, Tracker>> = {}

  private store = useObservationsStore()

  private registerProgressCb(patientId: string, codeKey: string, cb: ((items: any[], code: string) => void) | null) {
    if (!this.pausedTracker[patientId]) this.pausedTracker[patientId] = {}
    if (!this.pausedTracker[patientId][codeKey]) return
    this.pausedTracker[patientId][codeKey].progressCallBack = cb
  }

  private unregisterProgressCb(patientId: string, codeKey?: string) {
    if (!this.pausedTracker[patientId]) return
    if (codeKey) {
      if (this.pausedTracker[patientId][codeKey]) {
        this.pausedTracker[patientId][codeKey].progressCallBack = null
      }
    } else {
      Object.values(this.pausedTracker[patientId]).forEach((t) => (t.progressCallBack = null))
    }
  }

  disposePatient(patientId: string) {
    // abort requests
    Object.keys(this.abortControllers).forEach((key) => {
      try {
        this.abortControllers[key].abort(`disposePatient ${patientId}`)
      } catch {}
      delete this.abortControllers[key]
    })
    // clear callbacks
    if (this.pausedTracker[patientId]) {
      Object.keys(this.pausedTracker[patientId]).forEach((codeKey) => {
        this.unregisterProgressCb(patientId, codeKey)
      })
      delete this.pausedTracker[patientId]
    }
  }

  async getMetrics(patientId: string, from: string, to: string): Promise<EpisodeOfCareMetric> {
    return await this.call<any>('GET', `${this.PATH_METRICS}/${patientId}/episode-of-care`, undefined, {
      params: {
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        annotationTypes:
          'patient_call,PPS_follow-up_note,professional_in_charge_call,family_circle_call,home_visit,virtual_visit,teleconsultation,medical_office_consultation,tele-expertise',
        dailyWindowStartHour: 8,
        dailyWindowEndHour: 20,
      },
    })
  }

  async get(patientId: string): Promise<Observation[]> {
    let arr: any[] = []
    let nextPageUrl = '?pageSize=1000'
    do {
      await this.call<any>('GET', `${this.PATH_OBSERVATIONS}/${patientId}${nextPageUrl}`).then((response) => {
        arr = arr.concat(response.items)
        nextPageUrl = response.nextPageUrl
      })
    } while (nextPageUrl)
    return arr
  }

  async getCount(patientId: string, codes: string[], codesExams: string[], countBy: string): Promise<any> {
    let arr: any[] = []
    let params: any = {}
    let url = `${this.PATH_OBSERVATIONS_COUNT}`

    params = { patientId }
    if (codes.length) params.codes = codes.join(',') + ',' + codesExams.join(',')
    if (countBy) params.countBy = countBy

    await this.call<any>('GET', `${url}`, undefined, { params }).then((response) => {
      arr = arr.concat(response.items)
    })

    return arr
  }

  async getCodes(patientId: string): Promise<any> {
    let arr: any[] = []
    let params: any = {}
    let url = `${this.PATH_OBSERVATIONS_CODES}`

    params = { patientId }

    await this.call<any>('GET', `${url}`, undefined, { params }).then((response) => {
      arr = arr.concat(response.items)
    })

    return arr
  }

  async getCardioline(patientId: string): Promise<any> {
    let arr: any[] = []
    let url = `${this.PATH_OBSERVATIONS_CARDIOLINE}/${patientId}`
    await this.call<any>('GET', `${url}`).then((response) => {
      arr = arr.concat(response.items)
    })
    return arr
  }

  async getStethoMeMediaToken(): Promise<any> {
    let token: string = ''
    let url = `${this.PATH_OBSERVATIONS_STETHOME}/token`
    await this.call<any>('GET', `${url}`).then((response) => {
      token = response.temporaryMediaToken
    })
    return token
  }

  async getStethoMeBodyUrl(auscultationType: string, visitId: string): Promise<any> {
    let token = await this.getStethoMeMediaToken()
    return `${this.PATH_STETHOME}/${auscultationType}/frame-body/${visitId}/0/fr?bearer=${token}`
  }

  async getStethoMePartUrl(auscultationType: string, visitId: string, point: string): Promise<any> {
    let token = await this.getStethoMeMediaToken()
    return `${this.PATH_STETHOME}/${auscultationType}/frame/${visitId}/${point}/fr?bearer=${token}`
  }

  abortObservationByPage() {
    if (this.getByPageController) this.getByPageController.abort()
  }

  abortObservationLoading(list: string[] | null = null) {
    if (list) {
      list.forEach((code) => {
        if (this.abortControllers[code]) {
          this.abortControllers[code].abort(`Abort loading for ${code}`)
          delete this.abortControllers[code]
        }
      })
    } else {
      Object.keys(this.abortControllers).forEach((key) => {
        try {
          this.abortControllers[key].abort(`Abort loading for ${key}`)
        } catch {}
      })
      this.abortControllers = {}
    }
  }

  cleanUp() {
    Object.keys(this.pausedTracker).forEach((patientId) => {
      this.disposePatient(patientId)
    })
  }

  async getAllByPage(
    patientId: string,
    codes: string[],
    devices: string[],
    nextPageUrl: string,
  ): Promise<{ nextPageUrl: string; observations: Observation[] }> {
    this.getByPageController = new AbortController()
    let arr: any[] = []
    let params: any = {}
    let url = `${this.PATH_OBSERVATIONS}${nextPageUrl}`
    if (!nextPageUrl.includes('readAfterKey')) {
      params.patientId = patientId
      if (codes.length) params.codes = codes
      if (devices.length) params.deviceDefinitions = devices
      params.orderDirection = 'desc'
    }
    await this.call<any>('GET', `${url}`, undefined, { params }, this.getByPageController.signal).then((response) => {
      arr = arr.concat(response.items)
      nextPageUrl = response.nextPageUrl ? decodeURIComponent(response.nextPageUrl) : response.nextPageUrl
    })
    return { nextPageUrl, observations: arr }
  }

  async getByPage(
    patientId: string,
    codes: string[],
    devices: string[],
    from: Date,
    to: Date,
    nextPageUrl: string,
  ): Promise<{ nextPageUrl: string; observations: Observation[] }> {
    this.getByPageController = new AbortController()
    let arr: any[] = []
    let params: any = {}
    let url = `${this.PATH_OBSERVATIONS}${nextPageUrl}`
    if (!nextPageUrl.includes('readAfterKey')) {
      params.patientId = patientId
      if (codes.length) params.codes = codes
      if (devices.length) params.deviceDefinitions = devices
      if (from) params.effectiveFrom = from.toISOString()
      if (to) params.effectiveUpTo = to.toISOString()
      params.orderDirection = 'desc'
    }
    await this.call<any>('GET', `${url}`, undefined, { params }, this.getByPageController.signal).then((response) => {
      arr = arr.concat(response.items)
      nextPageUrl = response.nextPageUrl ? decodeURIComponent(response.nextPageUrl) : response.nextPageUrl
    })
    return { nextPageUrl, observations: arr }
  }

  async getByCodeAndPage(
    patientId: string,
    code: string[],
    fromDateFirstLimit: Date,
    from: Date,
    to: Date,
    progressCallBack: Function,
  ): Promise<{ observations: Observation[] | null }> {
    let arr: any[] = []
    const codeKey = code.join('_')
    let params: any = {}
    const url = `${this.PATH_OBSERVATIONS}`
    params.codes = code
    params.patientId = patientId
    if (fromDateFirstLimit) params.effectiveFrom = from.toISOString()
    if (to) params.effectiveUpTo = to.toISOString()
    params.orderDirection = 'desc'

    let nextPageUrl: string | null = `?pageSize=${this.PAGE_SIZE}`

    if (!this.pausedTracker[patientId]) this.pausedTracker[patientId] = {}
    if (!this.pausedTracker[patientId][codeKey]) {
      this.pausedTracker[patientId][codeKey] = {
        paused: true,
        code,
        from,
        to,
        hasReachedTwoWeeks: false,
        progressCallBack: null,
      }
    }

    this.registerProgressCb(patientId, codeKey, progressCallBack as any)

    if (!this.abortControllers[codeKey]) this.abortControllers[codeKey] = new AbortController()

    try {
      if (nextPageUrl) {
        do {
          const response = await this.call<any>(
            'GET',
            `${url}/${nextPageUrl}`,
            undefined,
            { params },
            this.abortControllers[codeKey].signal,
          )
          arr = response.items
          nextPageUrl = response.nextPageUrl ? decodeURIComponent(response.nextPageUrl) : response.nextPageUrl

          if (arr.length) {
            this.pausedTracker[patientId][codeKey].to = new Date(arr[arr.length - 1].effectiveAt)
          }

          if (!nextPageUrl) {
            this.pausedTracker[patientId][codeKey].hasReachedTwoWeeks = true
            this.pausedTracker[patientId][codeKey].paused = false
          }

          if (arr.length) {
            const lastValue = new Date(arr[arr.length - 1].effectiveAt).getTime()
            const toValue = fromDateFirstLimit.getTime()
            if (lastValue < toValue && this.pausedTracker[patientId][codeKey].paused === true) {
              nextPageUrl = null
              this.pausedTracker[patientId][codeKey].hasReachedTwoWeeks = true
            }
          }

          const cb = this.pausedTracker[patientId][codeKey]?.progressCallBack
          cb?.(arr, code[0])
        } while (nextPageUrl)
      }
      return { observations: arr }
    } finally {
      if (!this.pausedTracker[patientId][codeKey]?.paused) {
        this.unregisterProgressCb(patientId, codeKey)
      }
    }
  }

  continueLoading(patientId: string, displayedSeries: []) {
    Object.keys(this.pausedTracker[patientId] || {}).forEach((key) => {
      if (
        this.pausedTracker[patientId][key].paused &&
        displayedSeries.filter((d: any) => d.definition.code === key).length
      ) {
        this.pausedTracker[patientId][key].paused = false
        if (this.pausedTracker[patientId][key].hasReachedTwoWeeks) {
          const { code, from, to, progressCallBack } = this.pausedTracker[patientId][key]
          if (progressCallBack) {
            this.getByCodeAndPage(patientId, code, from, from, to, progressCallBack)
          }
        }
      }
    })
  }

  async getLatestObservationByCode(patientId: string, code: string): Promise<Observation[]> {
    return await this.call<any>(
      'GET',
      `${this.PATH_OBSERVATIONS}?codes=${code}&patientId=${patientId}&orderDirection=desc&firstByCode=true`,
    ).then((response) => {
      return response.items
    })
  }

  async getListOfDocumentObservation(patientId: string, documentId: string): Promise<Observation[]> {
    return await this.call<any>(
      'GET',
      `${this.PATH_OBSERVATIONS}?patientId=${patientId}&derivedFromType=document&derivedFromId=${documentId}`,
    ).then((response) => {
      return response.items
    })
  }
}

export default ObservationsModule
